import { useCallback, useEffect, useRef, useState } from 'react';

import { Tooltip as MuiTooltip } from '@mui/material';

import { useCascadeState } from '../../utils';
import { TooltipTitle } from '../TooltipTitle';
import { hasVisibleTooltipMessage, resolvePopperSlotProps } from './utils';

import type { SyntheticEvent } from 'react';
import type { PopperProps } from '@mui/material';
import type { TooltipProps } from './types';

const Tooltip = (props: TooltipProps) => {
  const {
    message,
    link,
    startIcon,
    endIcon,
    ref,
    children,
    mountOnEmptyMessage = false,
    placement = 'top',
    interactive = false,
    describeChild = true,
    color = 'default',
    arrow,
    wordBreak,
    whiteSpace,
    slotProps,
    closeOnScroll = true,
    open: openProp,
    onOpen,
    onClose,
    ...others
  } = props;

  const [scrollClosed, setScrollClosed] = useState(false);
  const previousOpenPropRef = useRef(openProp);
  const [tooltipOpen, setTooltipOpen] = useCascadeState<boolean, [SyntheticEvent | Event]>({
    initialValue: false,
    value: openProp,
    onChange: (value, event) => {
      if (value) {
        onOpen?.(event as SyntheticEvent);
        return;
      }
      onClose?.(event);
    },
  });
  const hasVisibleMessage = hasVisibleTooltipMessage(message);
  const resolvedOpen = tooltipOpen && !scrollClosed && hasVisibleMessage;

  const handleOpen = useCallback(
    (event: SyntheticEvent) => {
      setScrollClosed(false);
      setTooltipOpen(true, event);
    },
    [setTooltipOpen],
  );

  const handleClose = useCallback(
    (event: SyntheticEvent | Event) => {
      setTooltipOpen(false, event);
    },
    [setTooltipOpen],
  );

  useEffect(() => {
    const previousOpenProp = previousOpenPropRef.current;
    previousOpenPropRef.current = openProp;

    if (!previousOpenProp && openProp) {
      setScrollClosed(false);
    }
  }, [openProp]);

  useEffect(() => {
    if (!closeOnScroll || !resolvedOpen) {
      return;
    }

    const handleScroll = (event: Event) => {
      setScrollClosed(true);
      setTooltipOpen(false, event);
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [closeOnScroll, resolvedOpen, setTooltipOpen]);

  const unmountTooltip = !mountOnEmptyMessage && !hasVisibleMessage;

  const defaultPopperSlotProps = {
    sx: scrollClosed ? { display: 'none' } : undefined,
    popperOptions: { strategy: 'fixed' as const },
  } satisfies Partial<PopperProps>;
  const popperSlotProps = resolvePopperSlotProps(slotProps, defaultPopperSlotProps);

  if (unmountTooltip) {
    return children;
  }
  return (
    <MuiTooltip
      ref={ref}
      title={
        <TooltipTitle
          message={message}
          link={link}
          startIcon={startIcon}
          endIcon={endIcon}
          wordBreak={wordBreak}
          whiteSpace={whiteSpace}
        />
      }
      placement={placement}
      disableInteractive={!link && !interactive}
      describeChild={describeChild}
      color={color}
      arrow={!message ? false : arrow}
      open={resolvedOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      slotProps={{
        ...slotProps,
        popper: popperSlotProps,
      }}
      {...others}
    >
      {children}
    </MuiTooltip>
  );
};

export default Tooltip;
